/**
 * Project tasks collection — typed CRUD on top of the generic store.
 *
 * Tasks are the PM primitive layered over Workflows: a to-do with an
 * owner, due date, checklist, optional recurrence, and an append-only
 * timeline. This adapter centralises timeline appending, the
 * recurrence spawn (completing a recurring task mints the next
 * instance), and checklist mutation. Modelled on `tickets.ts`.
 */

import { db, nowIso, uuid } from './store';
import type {
  ProjectTask,
  TaskChecklistItem,
  TaskEvent,
  TaskPriority,
  TaskRecurrence,
  TaskStatus,
  TaskTimeEntry,
  WorkAttachment,
  WorkComment,
} from './types';
import { seedTasks } from './seed';

/** Roster of valid assignees — humans plus Claude agents. */
export const TASK_ASSIGNEES = [
  'Trent',
  'Grant',
  'Patrick',
  'claude:worker',
  'claude:fixer',
  'claude:researcher',
] as const;

const event = (
  kind: TaskEvent['kind'],
  actor: string,
  text: string,
  at = nowIso(),
): TaskEvent => ({ id: uuid(), at, actor, kind, text });

export async function listTasks(): Promise<ProjectTask[]> {
  return db.list('tasks');
}

export async function getTask(id: string): Promise<ProjectTask | null> {
  return db.get('tasks', id);
}

export function subscribeTasks(fn: (records: ProjectTask[]) => void): () => void {
  return db.subscribe('tasks', fn);
}

export interface NewTaskInput {
  title: string;
  notes?: string;
  priority?: TaskPriority;
  assignee?: string | null;
  dueDate?: string | null;
  recurrence?: TaskRecurrence;
  checklist?: { text: string }[];
  projectId?: string;
  projectName?: string;
  accountId?: string;
  accountName?: string;
  workflowId?: string;
  workflowName?: string;
  personId?: string;
  personName?: string;
  tags?: string[];
  createdBy?: string;
}

export async function createTask(input: NewTaskInput): Promise<ProjectTask> {
  const ts = nowIso();
  const createdBy = input.createdBy ?? 'You';
  const record: ProjectTask = {
    id: uuid(),
    title: input.title,
    notes: input.notes,
    status: 'todo',
    priority: input.priority ?? 'medium',
    assignee: input.assignee ?? null,
    dueDate: input.dueDate ?? null,
    recurrence: input.recurrence ?? 'none',
    checklist: (input.checklist ?? []).map((c) => ({
      id: uuid(),
      text: c.text,
      done: false,
    })),
    projectId: input.projectId,
    projectName: input.projectName,
    tags: input.tags,
    accountId: input.accountId,
    accountName: input.accountName,
    workflowId: input.workflowId,
    workflowName: input.workflowName,
    personId: input.personId,
    personName: input.personName,
    completedAt: null,
    createdBy,
    timeline: [event('created', createdBy, 'Created task', ts)],
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('tasks', record);
  return record;
}

/**
 * Patch a task. Re-stamps `updatedAt` and appends timeline events for
 * status / assignee changes plus a catch-all for other field edits.
 */
export async function updateTask(
  id: string,
  patch: Partial<Omit<ProjectTask, 'id' | 'createdAt' | 'timeline' | 'checklist'>>,
  actor = 'You',
): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  const ts = nowIso();

  const events: TaskEvent[] = [];
  if (patch.status && patch.status !== existing.status) {
    events.push(
      event('status', actor, `Status: ${existing.status} → ${patch.status}`, ts),
    );
  }
  if (patch.assignee !== undefined && patch.assignee !== existing.assignee) {
    events.push(
      event(
        'assigned',
        actor,
        `Assignee: ${existing.assignee ?? '—'} → ${patch.assignee ?? '—'}`,
        ts,
      ),
    );
  }
  const otherKeys = Object.keys(patch).filter(
    (k) => k !== 'status' && k !== 'assignee' && k !== 'updatedAt' && k !== 'completedAt',
  );
  if (otherKeys.length > 0) {
    events.push(event('note', actor, `Updated ${otherKeys.join(', ')}`, ts));
  }

  return db.update('tasks', id, {
    ...patch,
    timeline: [...existing.timeline, ...events],
    updatedAt: ts,
  });
}

/** Roll an ISO date (YYYY-MM-DD) forward by one recurrence cadence. */
function rollDueDate(due: string | null, recurrence: TaskRecurrence): string | null {
  if (!due || recurrence === 'none') return due;
  const d = new Date(`${due}T00:00:00`);
  if (Number.isNaN(d.getTime())) return due;
  if (recurrence === 'daily') d.setDate(d.getDate() + 1);
  else if (recurrence === 'weekly') d.setDate(d.getDate() + 7);
  else if (recurrence === 'monthly') d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Set a task's status with a timeline entry. Completing a task stamps
 * `completedAt`; completing a *recurring* task also spawns the next
 * instance (fresh checklist, due date rolled forward) so the cadence
 * continues while the finished copy stays in history.
 */
export async function setTaskStatus(
  id: string,
  status: TaskStatus,
  actor = 'You',
): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  const ts = nowIso();
  const completing = status === 'done' && existing.status !== 'done';

  const updated = await db.update('tasks', id, {
    status,
    completedAt: status === 'done' ? ts : null,
    timeline: [
      ...existing.timeline,
      event('status', actor, `Status: ${existing.status} → ${status}`, ts),
    ],
    updatedAt: ts,
  });

  if (completing && existing.recurrence !== 'none') {
    const nextDue = rollDueDate(existing.dueDate, existing.recurrence);
    const spawn: ProjectTask = {
      ...existing,
      id: uuid(),
      status: 'todo',
      dueDate: nextDue,
      completedAt: null,
      checklist: existing.checklist.map((c) => ({ ...c, id: uuid(), done: false })),
      timeline: [
        event(
          'recurred',
          actor,
          `Recurred from completed ${existing.recurrence} task`,
          ts,
        ),
      ],
      createdAt: ts,
      updatedAt: ts,
    };
    await db.add('tasks', spawn);
  }

  return updated;
}

/** Add a checklist item + a `checklist` timeline entry. */
export async function addChecklistItem(
  id: string,
  text: string,
  actor = 'You',
): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  const ts = nowIso();
  const item: TaskChecklistItem = { id: uuid(), text, done: false };
  return db.update('tasks', id, {
    checklist: [...existing.checklist, item],
    timeline: [...existing.timeline, event('checklist', actor, `Added subtask`, ts)],
    updatedAt: ts,
  });
}

/** Toggle a checklist item's done state. */
export async function toggleChecklistItem(
  id: string,
  itemId: string,
): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  return db.update('tasks', id, {
    checklist: existing.checklist.map((c) =>
      c.id === itemId ? { ...c, done: !c.done } : c,
    ),
    updatedAt: nowIso(),
  });
}

/** Remove a checklist item. */
export async function removeChecklistItem(
  id: string,
  itemId: string,
): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  return db.update('tasks', id, {
    checklist: existing.checklist.filter((c) => c.id !== itemId),
    updatedAt: nowIso(),
  });
}

export async function deleteTask(id: string): Promise<void> {
  await db.remove('tasks', id);
}

// ---------------------------------------------------------------------------
// Collaboration + time (inline arrays, same shape projects use).
// ---------------------------------------------------------------------------

/** Add a threaded comment. `mentions` are the roster names @tagged in the body. */
export async function addTaskComment(
  id: string,
  authorName: string,
  body: string,
  mentions: string[] = [],
  actor = authorName,
): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  const ts = nowIso();
  const comment: WorkComment = { id: uuid(), authorName, body, mentions, createdAt: ts };
  return db.update('tasks', id, {
    comments: [...(existing.comments ?? []), comment],
    timeline: [...existing.timeline, event('note', actor, 'Commented', ts)],
    updatedAt: ts,
  });
}

/** Pin a file or link attachment. */
export async function addTaskAttachment(
  id: string,
  attachment: Omit<WorkAttachment, 'id' | 'addedAt'>,
): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  const ts = nowIso();
  const record: WorkAttachment = { ...attachment, id: uuid(), addedAt: ts };
  return db.update('tasks', id, {
    attachments: [...(existing.attachments ?? []), record],
    timeline: [...existing.timeline, event('note', attachment.addedBy, `Attached ${attachment.label}`, ts)],
    updatedAt: ts,
  });
}

export async function removeTaskAttachment(id: string, attachmentId: string): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  return db.update('tasks', id, {
    attachments: (existing.attachments ?? []).filter((a) => a.id !== attachmentId),
    updatedAt: nowIso(),
  });
}

/** Log a time entry (minutes) against a task. */
export async function addTaskTimeEntry(
  id: string,
  entry: { who: string; minutes: number; note?: string },
): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  const ts = nowIso();
  const record: TaskTimeEntry = { id: uuid(), who: entry.who, minutes: entry.minutes, note: entry.note, at: ts };
  return db.update('tasks', id, {
    timeEntries: [...(existing.timeEntries ?? []), record],
    timeline: [...existing.timeline, event('note', entry.who, `Logged ${entry.minutes}m`, ts)],
    updatedAt: ts,
  });
}

export async function removeTaskTimeEntry(id: string, entryId: string): Promise<ProjectTask> {
  const existing = await db.get('tasks', id);
  if (!existing) throw new Error(`No task ${id}`);
  return db.update('tasks', id, {
    timeEntries: (existing.timeEntries ?? []).filter((e) => e.id !== entryId),
    updatedAt: nowIso(),
  });
}

/** Seed the tasks collection on first run. Idempotent. */
export async function bootstrapTasksIfEmpty(): Promise<void> {
  const existing = await db.list('tasks');
  if (existing.length > 0) return;
  await db.replaceAll('tasks', seedTasks);
}

/** Reset the tasks collection back to seed data. Useful for dev. */
export async function resetTasksToSeed(): Promise<void> {
  await db.replaceAll('tasks', seedTasks);
}
